/**
 * Admin User Management API
 *
 * Requires admin session token (X-Session-Token with admin privileges).
 * All calls go to /admin/users/* endpoints.
 */

import api from "./index";
import ENDPOINTS from "./endpoints";
import logger from "../services/Logger";

/**
 * List all users in the organisation.
 * @returns {Promise<Array>} Array of user objects
 */
export const listAdminUsers = async () => {
  try {
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_LIST_USERS,
    });
    return response.data.users;
  } catch (error) {
    logger.error("adminUsers", "Error listing users", { error: error.message });
    throw error;
  }
};

/**
 * Invite a new user to the organisation.
 * @param {string} email
 * @param {string} name
 * @param {boolean} orgMode - Whether to grant admin privileges
 * @returns {Promise<{user_id: string, email: string, invite_link: string, email_sent: boolean}>}
 */
export const inviteAdminUser = async (email, name, orgMode = false, allowExternal = false) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_INVITE_USER,
      data: { email, name, org_mode: orgMode, allow_external: allowExternal },
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error inviting user", { error: error.message, email });
    throw error;
  }
};

/**
 * Disable a user's account (soft-disable, not hard-delete).
 * @param {string} userId
 * @returns {Promise<{message: string}>}
 */
export const disableAdminUser = async (userId) => {
  try {
    const response = await api({
      method: "DELETE",
      url: ENDPOINTS.ADMIN_DISABLE_USER(userId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error disabling user", { error: error.message, userId });
    throw error;
  }
};

/**
 * Update a user's admin role (org_mode).
 * @param {string} userId
 * @param {boolean} orgMode
 * @returns {Promise<{message: string}>}
 */
/**
 * Re-enable a previously disabled user.
 * @param {string} userId
 * @returns {Promise<{message: string}>}
 */
export const restoreAdminUser = async (userId) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_RESTORE_USER(userId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error restoring user", { error: error.message, userId });
    throw error;
  }
};

/**
 * Hard-delete an unaccepted invite (removes Firebase Auth user + Firestore doc).
 * @param {string} userId
 * @returns {Promise<{message: string}>}
 */
export const cancelAdminUserInvite = async (userId) => {
  try {
    const response = await api({
      method: "DELETE",
      url: ENDPOINTS.ADMIN_CANCEL_INVITE(userId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error cancelling invite", { error: error.message, userId });
    throw error;
  }
};

/**
 * Permanently purge a disabled user and all their data.
 * Returns 202 — the purge runs asynchronously in the background.
 * @param {string} userId
 * @returns {Promise<{message: string}>}
 */
export const purgeAdminUser = async (userId) => {
  try {
    const response = await api({
      method: "DELETE",
      url: ENDPOINTS.ADMIN_PURGE_USER(userId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error purging user", { error: error.message, userId });
    throw error;
  }
};

export const updateAdminUserOrgMode = async (userId, orgMode) => {
  try {
    const response = await api({
      method: "PATCH",
      url: ENDPOINTS.ADMIN_UPDATE_USER(userId),
      data: { org_mode: orgMode },
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error updating user org_mode", { error: error.message, userId });
    throw error;
  }
};

/**
 * Update a user's display name.
 * @param {string} userId
 * @param {string} name
 * @returns {Promise<{message: string}>}
 */
export const updateAdminUserName = async (userId, name) => {
  try {
    const response = await api({
      method: "PUT",
      url: ENDPOINTS.ADMIN_UPDATE_USER_NAME(userId),
      data: { name },
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error updating user name", { error: error.message, userId });
    throw error;
  }
};

/**
 * Get organisation-level settings.
 * @returns {Promise<{signup_mode: string}>}
 */
/**
 * Send a password reset email to a non-SSO user.
 * @param {string} userId
 * @returns {Promise<{message: string}>}
 */
export const resetAdminUserPassword = async (userId) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_RESET_USER_PASSWORD(userId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error sending password reset", { error: error.message, userId });
    throw error;
  }
};

/**
 * Resend an invite to a user who has not yet accepted.
 * @param {string} userId
 * @returns {Promise<{user_id: string, email: string, invite_link: string, email_sent: boolean}>}
 */
export const resendAdminUserInvite = async (userId) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_RESEND_INVITE(userId),
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error resending invite", { error: error.message, userId });
    throw error;
  }
};

export const getOrgSettings = async () => {
  try {
    const response = await api({ method: "GET", url: ENDPOINTS.ADMIN_ORG_SETTINGS });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error getting org settings", { error: error.message });
    throw error;
  }
};

/**
 * Update organisation-level settings.
 * @param {Object} opts
 * @param {string} [opts.signupMode] - "domain_open" | "invite_only" | "public"
 * @param {boolean} [opts.allowPurge] - whether admins can permanently purge users
 * @param {boolean} [opts.clientIntegrationEnabled] - whether the Client Integration feature is enabled for this org
 * @returns {Promise<{signup_mode: string, allow_purge: boolean, client_integration_enabled: boolean}>}
 */
export const updateOrgSettings = async ({ signupMode, allowPurge, clientIntegrationEnabled, ssoProviders } = {}) => {
  try {
    const data = {};
    if (signupMode !== undefined) data.signup_mode = signupMode;
    if (allowPurge !== undefined) data.allow_purge = allowPurge;
    if (clientIntegrationEnabled !== undefined) data.client_integration_enabled = clientIntegrationEnabled;
    if (ssoProviders !== undefined) data.sso_providers = ssoProviders;
    const response = await api({
      method: "PATCH",
      url: ENDPOINTS.ADMIN_ORG_SETTINGS,
      data,
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error updating org settings", { error: error.message });
    throw error;
  }
};

export const provisionBreakGlassAccount = async (email, name) => {
  try {
    const response = await api({
      method: "POST",
      url: ENDPOINTS.ADMIN_BREAK_GLASS_ACCOUNTS,
      data: { email, name },
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error provisioning break glass account", {
      error: error.message,
      email,
    });
    throw error;
  }
};

export const revokeBreakGlassAccount = async (email) => {
  try {
    const response = await api({
      method: "DELETE",
      url: ENDPOINTS.ADMIN_BREAK_GLASS_ACCOUNT(email),
    });
    return response.data;
  } catch (error) {
    logger.error("adminUsers", "Error revoking break glass account", {
      error: error.message,
      email,
    });
    throw error;
  }
};
